// ============================================
// FILE: frontend/src/components/messaging/AdminChatMonitor.jsx
// PURPOSE: Admin view of full conversation between two parties
// FIXED: Added auto-refresh polling for real-time message updates
// ============================================

import { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Loader2, Eye, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// MAIN COMPONENT
// ========================================

export function AdminChatMonitor({
  chat,
  loading = false,
  error = null,
  onRefresh, // NEW: Callback to refresh chat data
  autoRefreshInterval = 5000 // NEW: Default 5 seconds
}) {
  console.log('[AdminChatMonitor] Rendering with chat:', {
    connectionId: chat?.id,
    messageCount: chat?.messageCount,
    loading
  });

  // ========================================
  // REFS FOR POLLING
  // ========================================

  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // ========================================
  // AUTO-REFRESH POLLING
  // ========================================

  useEffect(() => {
    // Only start polling if we have a chat and onRefresh callback
    if (!chat?.id || !onRefresh) {
      console.log('[AdminChatMonitor] No chat or onRefresh, skipping polling');
      return;
    }

    // Prevent duplicate polling
    if (isPollingRef.current) {
      console.log('[AdminChatMonitor] Polling already active');
      return;
    }

    console.log('[AdminChatMonitor] 🔄 Starting auto-refresh polling...', {
      connectionId: chat.id,
      interval: autoRefreshInterval
    });

    isPollingRef.current = true;

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      console.log('[AdminChatMonitor] ⏱️ Polling tick - refreshing chat');
      onRefresh(chat.id);
    }, autoRefreshInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log('[AdminChatMonitor] Cleanup: stopping polling');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [chat?.id, onRefresh, autoRefreshInterval]);

  // ========================================
  // LOADING STATE
  // ========================================

  if (loading && !chat) {
    console.log('[AdminChatMonitor] Initial loading state');
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading conversation...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================

  if (error) {
    console.error('[AdminChatMonitor] Error state:', error);
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <p className="text-sm text-red-800">{error}</p>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // EMPTY STATE
  // ========================================

  if (!chat) {
    console.log('[AdminChatMonitor] Empty state - no chat');
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">Select a connection to view conversation</p>
        </CardContent>
      </Card>
    );
  }

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  const getStatusColor = (status) => {
    console.log('[AdminChatMonitor] Getting status color for:', status);
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'both_contacted':
        return 'bg-blue-100 text-blue-800';
      case 'deal_in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'deal_completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'deal_failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    console.log('[AdminChatMonitor] Formatting status:', status);
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // ========================================
  // RENDER
  // ========================================

  console.log('[AdminChatMonitor] Rendering chat view');

  return (
    <Card className="h-full flex flex-col">
      {/* ========================================
          HEADER
          ======================================== */}
      <CardHeader className="border-b pb-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Conversation Monitor</CardTitle>
            </div>
            
            {/* Auto-refresh indicator */}
            {isPollingRef.current && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Auto-refreshing</span>
              </div>
            )}
          </div>

          {/* Parties Info */}
          <div className="grid grid-cols-2 gap-4">
            {/* Landowner */}
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs font-semibold text-muted-foreground mb-2">LANDOWNER</p>
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={chat.landowner?.photoURL} />
                  <AvatarFallback>
                    {chat.landowner?.fullName?.[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {chat.landowner?.fullName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {chat.landowner?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Investor */}
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs font-semibold text-muted-foreground mb-2">INVESTOR</p>
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={chat.investor?.photoURL} />
                  <AvatarFallback>
                    {chat.investor?.fullName?.[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {chat.investor?.fullName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {chat.investor?.email}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status and Score */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(chat.status)}>
                {formatStatus(chat.status)}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Match Score: {chat.matchScore}%
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {chat.messageCount} messages
            </span>
          </div>
        </div>
      </CardHeader>

      {/* ========================================
          MESSAGES
          ======================================== */}
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Show subtle loading indicator while refreshing (but keep showing messages) */}
        {loading && chat.messages && (
          <div className="flex items-center justify-center py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Checking for new messages...</span>
            </div>
          </div>
        )}

        {!chat.messages || chat.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">No messages yet</p>
          </div>
        ) : (
          chat.messages.map((message, index) => (
            <div key={message.id || index} className="flex gap-3">
              {/* Avatar */}
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage
                  src={message.sender?.photoURL}
                  alt={message.sender?.fullName}
                />
                <AvatarFallback>
                  {message.sender?.fullName?.[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* Message */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-semibold">
                    {message.sender?.fullName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(message.createdAt), 'PPpp')}
                  </p>
                  {message.isRead && (
                    <Badge variant="outline" className="text-xs h-5 px-1.5">
                      Read {format(new Date(message.readAt), 'p')}
                    </Badge>
                  )}
                </div>

                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-sm break-words whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {/* ========================================
          ADMIN NOTES
          ======================================== */}
      {chat.adminNotes && (
        <div className="border-t p-4 bg-blue-50">
          <p className="text-xs font-semibold text-blue-900 mb-2">Admin Notes:</p>
          <p className="text-xs text-blue-800 whitespace-pre-wrap">
            {chat.adminNotes}
          </p>
        </div>
      )}
    </Card>
  );
}

export default AdminChatMonitor;